import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button, Tooltip, message, Modal, AutoComplete, Input } from "antd";
import { SettingOutlined, LoadingOutlined, CheckOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import axios from "axios";
import appConfig from "../../utils/config";
import AIGeneratorConfig from "./AIGeneratorConfig";

const GENERATING_MESSAGES = [
    "Analyzing course content...",
    "Crafting intelligent questions...",
    "Building thoughtful answers...",
    "Structuring FAQ format...",
    "Polishing the output...",
    "Almost ready..."
];

const DEFAULT_USER_PROMPT = "Generate FAQs a prospective student would ask before enrolling in this course.";

// Known stale/bogus prompts from older DB entries — auto-replace on load
const STALE_PROMPTS = [
    "Generate content for course context:",
    "Generate exactly 5 high-quality SEO-optimized FAQs for the following course."
];

const STALE_INSTRUCTIONS = [
    "Generate a comprehensive text block summarizing the key decision factors for this course. Output STRICTLY HTML format, appropriate for a rich text editor. Use paragraphs and bullet points."
];
const DEFAULT_CONTEXT_FIELDS = ["title", "headlines", "description"];
const MAX_HISTORY = 10;

// ── localStorage helpers for prompt history ──
const getHistoryKey = (category) => `ai_prompt_history_${category}`;

const loadPromptHistory = (category) => {
    try {
        const raw = localStorage.getItem(getHistoryKey(category));
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
};

const savePromptToHistory = (category, prompt) => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    const history = loadPromptHistory(category);
    // Remove duplicate if exists, then prepend
    const filtered = history.filter(h => h !== trimmed);
    filtered.unshift(trimmed);
    // Cap at MAX_HISTORY
    localStorage.setItem(getHistoryKey(category), JSON.stringify(filtered.slice(0, MAX_HISTORY)));
};

const recoverSkillsFromReasoning = (rawText) => {
    if (!rawText || typeof rawText !== "string") return [];
    const blocked = new Set([
        "goal",
        "input",
        "constraints",
        "constraint check",
        "output format",
        "title",
        "outcomes",
        "technical list",
        "non-technical list",
        "technical topics mentioned",
        "non-technical stuff",
        "programming languages",
        "outcomes say",
        "data structures",
        "algorithms"
    ]);
    const candidates = [];

    const addCandidate = (value) => {
        let skill = String(value || "")
            .replace(/^[\s"'`*-]+|[\s"',.;:`)-]+$/g, "")
            .replace(/\s+/g, " ")
            .trim();

        skill = skill.replace(/\s*\([^)]*\)\s*$/g, "").trim();
        skill = skill.replace(/\s*[→:-]\s*.*$/g, "").trim();
        skill = skill.replace(/^(and|or)\s+/i, "").trim();

        const lower = skill.toLowerCase();
        const wordCount = skill.split(/\s+/).filter(Boolean).length;
        if (!skill || wordCount > 5 || skill.length > 60) return;
        if (blocked.has(lower)) return;
        if (/^(the|a|an|i|it|so|but|however|maybe|better|probably|which|wait|goal|input|constraints?|output|title|outcomes?)\b/i.test(skill)) return;
        if (/[{}[\]]/.test(skill)) return;
        candidates.push(skill);
    };

    const candidateWindowMatch = rawText.match(
        /(?:Big O notation|Arrays|Hash Tables|Linked Lists|Stacks|Queues|Graphs|Recursion|Dynamic Programming)[\s\S]*$/i
    );
    const source = candidateWindowMatch ? candidateWindowMatch[0] : rawText;

    for (const match of source.matchAll(/^\s*\d+\.\s+(.+)/gm)) {
        let text = match[1].replace(/[*_]/g, '');
        if (text.toLowerCase().match(/^(goal|input|output|constraints|return only|no markdown|prefer|avoid|canonical|title|outcomes|curriculum|skills(\s*:|\s*$))/)) continue;
        addCandidate(text);
    }
    for (const match of source.matchAll(/^\s*[-*]\s+(.+)/gm)) {
        let text = match[1].replace(/[*_]/g, '');
        if (text.toLowerCase().match(/^(goal|input|output|constraints|return only|no markdown|prefer|avoid|canonical|title|outcomes|curriculum|skills(\s*:|\s*$))/)) continue;
        addCandidate(text);
    }
    for (const match of source.matchAll(/"([^"]{1,60})"/g)) {
        addCandidate(match[1]);
    }

    const seen = new Set();
    return candidates.filter((skill) => {
        const key = skill.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    }).slice(0, 12);
};

const AIGenerateAction = ({ 
    onGenerateSuccess, 
    contextInstructions, 
    systemPromptDefault, 
    buttonText = "Generate AI",
    courseId = "",
    category = "FAQ",
    mode = "json",      // "json" for FAQ-style, "text" for plain text (meta fields)
    compact = false,      // true = sparkle button only, no prompt input bar
    showText = false,
    defaultContextFields = null,  // Override default context fields per category
    excludeContextFields = []     // Fields to hide from the config modal checkboxes
}) => {
    const [configVisible, setConfigVisible] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatingStep, setGeneratingStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const intervalRef = useRef(null);
    
    const { accessToken, user } = useSelector((state) => state.auth);
    const token = accessToken || localStorage.getItem("accessToken");

    const [inlinePrompt, setInlinePrompt] = useState(DEFAULT_USER_PROMPT);
    const [isConfigLoaded, setIsConfigLoaded] = useState(false);
    const [dbDefault, setDbDefault] = useState("");        // What's currently saved in DB
    const [hasGenerated, setHasGenerated] = useState(false); // At least one generation done
    const [justSavedDefault, setJustSavedDefault] = useState(false); // Checkmark feedback
    const [promptHistory, setPromptHistory] = useState([]);

    // Load history from localStorage on mount
    useEffect(() => {
        setPromptHistory(loadPromptHistory(category));
    }, [category]);

    // Build AutoComplete options from history
    const historyOptions = useMemo(() => {
        if (!promptHistory.length) return [];
        return promptHistory.map((p, i) => ({
            value: p,
            label: (
                <div style={{ fontSize: 12, color: "#595959", lineHeight: "18px", padding: "2px 0" }}>
                    <span style={{ color: "#bfbfbf", marginRight: 6, fontSize: 10 }}>↻</span>
                    {p.length > 70 ? p.slice(0, 70) + "…" : p}
                </div>
            )
        }));
    }, [promptHistory]);

    const handleSaveAsDefault = async () => {
        if (!token) return;
        try {
            const configReq = await axios.get(`${appConfig.apiURL}/v2/admin/ai/config?category=${category}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const configData = configReq.data?.status && configReq.data?.results ? configReq.data.results : {};

            const response = await axios.put(`${appConfig.apiURL}/v2/admin/ai/config`, {
                category,
                model: configData.model || "@cf/meta/llama-3-8b-instruct",
                systemPrompt: configData.systemPrompt || systemPromptDefault || "",
                userPrompt: inlinePrompt.trim(),
                contextFields: configData.contextFields?.length > 0 ? configData.contextFields : (defaultContextFields || DEFAULT_CONTEXT_FIELDS),
                internalInstructions: configData.internalInstructions || ""
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data?.status === false) {
                throw new Error(response.data.message || "Backend rejected the save operation.");
            }

            setDbDefault(inlinePrompt.trim());
            setJustSavedDefault(true);
            setTimeout(() => setJustSavedDefault(false), 2500);
        } catch (err) {
            console.error("Failed to save AI prompt as default:", err);
            const detail = err.response?.data?.message || err.message || "Unknown error";
            message.error(`Failed to set default prompt: ${detail}`);
        }
    };

    // Fetch initial config to populate the inline prompt bar
    useEffect(() => {
        if (!token) return;
        let isMounted = true;
        const fetchInitialConfig = async () => {
            try {
                const configReq = await axios.get(`${appConfig.apiURL}/v2/admin/ai/config?category=${category}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (isMounted && configReq.data?.status && configReq.data?.results) {
                    const savedPrompt = configReq.data.results.userPrompt;
                    if (savedPrompt && !STALE_PROMPTS.includes(savedPrompt)) {
                        setInlinePrompt(savedPrompt);
                        setDbDefault(savedPrompt);
                    } else {
                        // DB has a stale/bogus prompt — use the fresh default instead
                        setInlinePrompt(DEFAULT_USER_PROMPT);
                        setDbDefault("");
                    }
                }
            } catch (err) {
                console.error("Failed to fetch initial AI config:", err);
            } finally {
                if (isMounted) setIsConfigLoaded(true);
            }
        };
        fetchInitialConfig();
        return () => { isMounted = false; };
    }, [token, category]);

    // Simulated progress animation during generation
    useEffect(() => {
        if (isGenerating) {
            setGeneratingStep(0);
            setProgress(0);
            intervalRef.current = setInterval(() => {
                setGeneratingStep(prev => Math.min(prev + 1, GENERATING_MESSAGES.length - 1));
                setProgress(prev => Math.min(prev + (Math.random() * 12 + 3), 92));
            }, 2500);
        } else {
            if (intervalRef.current) clearInterval(intervalRef.current);
            setProgress(0);
            setGeneratingStep(0);
        }
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [isGenerating]);

    const handleGenerate = async () => {
        if (!token) {
            message.error("You must be logged in to generate AI content.");
            return;
        }
        if (!courseId) {
            message.error("Course ID is missing. Cannot generate content.");
            return;
        }

        setIsGenerating(true);

        try {
            // First fetch the latest configuration from the database
            const configReq = await axios.get(`${appConfig.apiURL}/v2/admin/ai/config?category=${category}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const configData = configReq.data?.status && configReq.data?.results ? configReq.data.results : {};
            
            const model = configData.model || "@cf/meta/llama-3-8b-instruct";
            const systemPrompt = configData.systemPrompt || systemPromptDefault || "";
            const userPrompt = inlinePrompt.trim() || configData.userPrompt || DEFAULT_USER_PROMPT;
            const contextFields = configData.contextFields?.length > 0 ? configData.contextFields : (defaultContextFields || DEFAULT_CONTEXT_FIELDS);
            
            // Prefer contextInstructions (from props) if the DB instructions are stale
            let cInstructions = configData.internalInstructions || contextInstructions || "";
            if (STALE_INSTRUCTIONS.includes(cInstructions.trim())) {
                cInstructions = contextInstructions || cInstructions;
            }

            // New payload format: backend assembles context from MongoDB
            const response = await axios.post(`${appConfig.apiURL}/v2/ai/generate`, {
                systemPrompt,
                userPrompt,
                contextInstructions: cInstructions,
                courseId,
                contextFields,
                modelOverride: model,
                category,
                mode // Track whether we expect JSON or plain text
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = response.data;
            if (!data.status) {
                throw new Error(data.message || "Failed to generate AI content");
            }

            let normalizedData = data.results?.normalized;

            // Prevent dummy templates from the prompt being treated as valid output
            if (category === "Skills" && normalizedData && typeof normalizedData === "object") {
                const sList = normalizedData.skills || normalizedData.skillList || normalizedData.skill_list || [];
                if (
                    sList === "Skill Name" || 
                    (Array.isArray(sList) && sList.length === 1 && sList[0] === "Skill Name") ||
                    (Array.isArray(sList) && sList.length === 0)
                ) {
                    console.log("Backend returned dummy normalized data. Falling back to reasoning block parser.");
                    normalizedData = null; // force fallback parsing
                }
            }

            if (mode === "json" && normalizedData && typeof normalizedData === "object") {
                savePromptToHistory(category, inlinePrompt);
                setPromptHistory(loadPromptHistory(category));
                setHasGenerated(true);

                setProgress(100);
                setTimeout(() => {
                    onGenerateSuccess(normalizedData);
                    message.success({ content: "Generated successfully!", key: "ai_gen", duration: 3 });
                }, 400);
                return;
            }

            const cfResult = data.results?.result;
            const resData = data.results || {};
            const firstChoice = cfResult?.choices?.[0];
            
            const rawText = 
                (typeof cfResult === 'string') ? cfResult : 
                (cfResult?.response || 
                 cfResult?.output || 
                 cfResult?.text || 
                 cfResult?.message?.content ||
                 cfResult?.message?.reasoning_content ||
                 cfResult?.message?.reasoning ||
                 cfResult?.choices?.[0]?.message?.content ||
                 firstChoice?.message?.reasoning_content ||
                 firstChoice?.message?.reasoning ||
                 firstChoice?.delta?.content ||
                 firstChoice?.delta?.reasoning_content ||
                 firstChoice?.delta?.reasoning ||
                 resData.response || 
                 resData.result || 
                 "");
            
            if (!rawText) {
                console.error("AI Generation Error: No response text found in payload structure", data);
                throw new Error("AI returned an empty or invalid response structure. Check console for details.");
            }

            // If the model (e.g., Llama 4) or backend already pre-parsed the JSON response into an object
            if (typeof rawText === 'object') {
                savePromptToHistory(category, inlinePrompt);
                setPromptHistory(loadPromptHistory(category));
                setHasGenerated(true);

                setProgress(100);
                setTimeout(() => {
                    onGenerateSuccess(rawText);
                    message.success({ content: "Generated successfully!", key: "ai_gen", duration: 3 });
                }, 400);
                return;
            }

            let cleanedText = String(rawText).trim();
            
            // Strip out <think>...</think> blocks generated by reasoning models (like DeepSeek) before parsing
            cleanedText = cleanedText.replace(/<think>[\s\S]*?<\/think>/g, '').trim();

            if (mode === "text") {
                // ── Text mode: return raw AI text (strip quotes if wrapped) ──
                if ((cleanedText.startsWith('"') && cleanedText.endsWith('"')) ||
                    (cleanedText.startsWith("'") && cleanedText.endsWith("'"))) {
                    cleanedText = cleanedText.slice(1, -1);
                }

                savePromptToHistory(category, inlinePrompt);
                setPromptHistory(loadPromptHistory(category));
                setHasGenerated(true);

                setProgress(100);
                setTimeout(() => {
                    onGenerateSuccess(cleanedText);
                    message.success({ content: "Generated successfully!", key: "ai_gen", duration: 3 });
                }, 400);
            } else {
                // ── JSON mode (FAQs): parse as structured data ──
                const jsonStart = Math.min(
                    cleanedText.indexOf("[") !== -1 ? cleanedText.indexOf("[") : Infinity,
                    cleanedText.indexOf("{") !== -1 ? cleanedText.indexOf("{") : Infinity
                );
                const jsonEnd = Math.max(
                    cleanedText.lastIndexOf("]"),
                    cleanedText.lastIndexOf("}")
                );

                let parsedJson;
                
                if (jsonStart !== Infinity && jsonEnd !== -1 && jsonEnd > jsonStart) {
                    let jsonText = cleanedText.substring(jsonStart, jsonEnd + 1);
                    try {
                        parsedJson = JSON.parse(jsonText);
                    } catch (jsonError) {
                        console.warn("Direct JSON parse failed, attempting recovery...", jsonError);
                        let repairedText = jsonText.replace(/\\"(?=\s*[}\]])/g, '"');
                        try {
                            parsedJson = JSON.parse(repairedText);
                        } catch (e2) {
                            const objectMatches = jsonText.match(/\{\s*"question"\s*:\s*"[^"]*"\s*,\s*"answer"\s*:\s*"[^"]*"\s*\}/g);
                            if (objectMatches && objectMatches.length > 0) {
                                parsedJson = objectMatches.map(m => JSON.parse(m));
                                message.warning({ content: `AI response was truncated. Recovered ${parsedJson.length} block(s).`, key: "ai_gen", duration: 6 });
                            }
                        }
                    }
                }

                // Prevent the frontend JSON extractor from being fooled by the dummy template in the reasoning text
                if (category === "Skills" && parsedJson) {
                    const sList = parsedJson.skills || parsedJson.skillList || parsedJson.skill_list || [];
                    if (
                        sList === "Skill Name" || 
                        (Array.isArray(sList) && sList.length === 1 && sList[0] === "Skill Name") ||
                        (Array.isArray(sList) && sList.length === 0)
                    ) {
                        console.log("Extracted JSON was just the dummy template. Falling back to reasoning text.");
                        parsedJson = null;
                    }
                }

                if (!parsedJson && category === "Skills") {
                    const recoveredSkills = recoverSkillsFromReasoning(rawText);
                    if (recoveredSkills.length > 0) {
                        parsedJson = { skills: recoveredSkills };
                        if (jsonStart !== Infinity) {
                            message.warning({ content: "JSON was malformed. Recovered skills from text.", key: "ai_gen", duration: 6 });
                        } else {
                            // If it never looked like JSON, we cleanly extracted from reasoning
                            // Note: we suppress the success message here because there's already a main success message at the bottom
                            console.log("Successfully extracted skills from AI reasoning block.");
                        }
                    }
                }

                if (!parsedJson) {
                    console.error("AI JSON Parsing Error (no recovery possible). Raw output:", rawText);
                    message.error({ content: "The AI response could not be parsed into the required format.", key: "ai_gen", duration: 6 });
                    return;
                }

                savePromptToHistory(category, inlinePrompt);
                setPromptHistory(loadPromptHistory(category));
                setHasGenerated(true);

                setProgress(100);
                setTimeout(() => {
                    onGenerateSuccess(parsedJson);
                    message.success({ content: "Generated successfully!", key: "ai_gen", duration: 3 });
                }, 400);
            }

        } catch (error) {
            console.error("AI Generation Error:", error);
            const errorMsg = error.response?.data?.message || error.message || "Unknown error";
            message.error({ content: `Error: ${errorMsg}`, key: "ai_gen", duration: 5 });
        } finally {
            setIsGenerating(false);
        }
    };

    // Should the "Set as default" link be visible?
    const showSetDefault = hasGenerated && inlinePrompt.trim() !== dbDefault;

    return (
        <>
            {/* ── Sparkle AI Button ── */}
            <style>{`
                @keyframes sparkle-rotate {
                    0% { transform: rotate(0deg) scale(1); }
                    25% { transform: rotate(8deg) scale(1.1); }
                    50% { transform: rotate(0deg) scale(1.02); }
                    75% { transform: rotate(-8deg) scale(1.1); }
                    100% { transform: rotate(0deg) scale(1); }
                }
                @keyframes sparkle-glow {
                    0%, 100% { filter: drop-shadow(0 0 1px rgba(59, 130, 246, 0.2)); }
                    50% { filter: drop-shadow(0 0 4px rgba(16, 185, 129, 0.4)); }
                }
                .ai-sparkle-btn:hover .sparkle-icon {
                    animation: sparkle-rotate 1.2s ease-in-out infinite, sparkle-glow 2s ease-in-out infinite;
                }
                .ai-sparkle-btn.generating .sparkle-icon {
                    animation: sparkle-rotate 0.8s linear infinite, sparkle-glow 1s ease-in-out infinite;
                }
                .ai-prompt-autocomplete .ant-select-selector {
                    border-radius: 6px !important;
                    height: 32px !important;
                    font-size: 13px !important;
                    box-shadow: inset 0 1px 2px rgba(0,0,0,0.02) !important;
                }
                .ai-prompt-autocomplete .ant-select-selection-search-input {
                    height: 30px !important;
                }
                .set-default-link {
                    font-size: 11px;
                    color: #8c8c8c;
                    cursor: pointer;
                    transition: color 0.2s;
                    user-select: none;
                    display: inline-flex;
                    align-items: center;
                    gap: 3px;
                }
                .set-default-link:hover {
                    color: #1890ff;
                }
            `}</style>
            
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ display: "flex", gap: 8 }}>
                    {!compact && (
                        <AutoComplete
                            className="ai-prompt-autocomplete"
                            options={historyOptions}
                            value={inlinePrompt}
                            onChange={val => setInlinePrompt(val)}
                            onSelect={val => setInlinePrompt(val)}
                            style={{ flex: "0 0 320px", width: 320, minWidth: 320, maxWidth: 320 }}
                            disabled={isGenerating || !isConfigLoaded}
                            popupMatchSelectWidth={360}
                            filterOption={false}
                        >
                            <Input
                                placeholder="Describe what to generate..."
                                onPressEnter={handleGenerate}
                                style={{
                                    height: 32,
                                    borderRadius: 6,
                                    borderColor: "#d9d9d9",
                                    fontSize: 13,
                                    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.02)"
                                }}
                            />
                        </AutoComplete>
                    )}
                    
                    <Tooltip title={buttonText || "Generate with AI"} placement="top">
                        <Button
                            className={`ai-sparkle-btn${isGenerating ? ' generating' : ''}`}
                            onClick={handleGenerate}
                            disabled={isGenerating}
                            style={{
                                width: (showText && buttonText) ? "auto" : 32,
                                minWidth: (showText && buttonText) ? "auto" : 32,
                                height: 32,
                                padding: (showText && buttonText) ? "0 12px" : 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 6,
                                border: "1px solid #d9d9d9",
                                background: "linear-gradient(135deg, #f0f5ff 0%, #f0fdf4 100%)",
                                boxShadow: "0 2px 0 rgba(0, 0, 0, 0.015)",
                                transition: "all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)",
                                gap: "8px"
                            }}
                        >
                            <svg className="sparkle-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ transition: "all 0.3s", flexShrink: 0 }}>
                                <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" 
                                      fill={`url(#sparkleGrad_${category})`} />
                                <path d="M19 2L19.75 4.25L22 5L19.75 5.75L19 8L18.25 5.75L16 5L18.25 4.25L19 2Z" 
                                      fill={`url(#sparkleGrad2_${category})`} opacity="0.8" />
                                <path d="M6 16L6.5 17.5L8 18L6.5 18.5L6 20L5.5 18.5L4 18L5.5 17.5L6 16Z" 
                                      fill={`url(#sparkleGrad2_${category})`} opacity="0.6" />
                                <defs>
                                    <linearGradient id={`sparkleGrad_${category}`} x1="4" y1="2" x2="20" y2="18" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#3B82F6" />
                                        <stop offset="1" stopColor="#10B981" />
                                    </linearGradient>
                                    <linearGradient id={`sparkleGrad2_${category}`} x1="16" y1="2" x2="22" y2="8" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#6366F1" />
                                        <stop offset="1" stopColor="#14B8A6" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            {showText && buttonText && (
                                <span style={{ 
                                    fontSize: "13px", 
                                    fontWeight: 500, 
                                    color: "#1f2937",
                                    whiteSpace: "nowrap"
                                }}>
                                    {buttonText}
                                </span>
                            )}
                        </Button>
                    </Tooltip>
                    
                    {!compact && (
                        <Tooltip title="AI Settings" placement="top">
                            <Button
                                onClick={() => setConfigVisible(true)}
                                disabled={isGenerating}
                                icon={<SettingOutlined style={{ fontSize: 14 }} />}
                                style={{
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 6,
                                    border: "1px solid #d9d9d9",
                                    background: "#fafafa",
                                    color: "#8c8c8c",
                                    boxShadow: "0 2px 0 rgba(0, 0, 0, 0.015)",
                                    transition: "all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)"
                                }}
                            />
                        </Tooltip>
                    )}
                </div>

                {/* ── Subtle "Set as default" link ── */}
                {!compact && showSetDefault && (
                    <div style={{ paddingLeft: 2, minHeight: 16 }}>
                        <span className="set-default-link" onClick={handleSaveAsDefault}>
                            {justSavedDefault ? (
                                <><CheckOutlined style={{ fontSize: 10, color: "#52c41a" }} /> Saved as default</>
                            ) : (
                                <>✦ Set as default</>
                            )}
                        </span>
                    </div>
                )}
            </div>

            {/* ── Generating Overlay Modal ── */}
            <Modal
                open={isGenerating}
                footer={null}
                closable={false}
                centered
                width={420}
                styles={{
                    mask: { backdropFilter: "blur(4px)", background: "rgba(0,0,0,0.25)" },
                    body: { padding: "32px 28px", textAlign: "center" }
                }}
            >
                <div style={{ marginBottom: 20 }}>
                    <LoadingOutlined style={{ fontSize: 40, color: "#722ed1" }} spin />
                </div>
                <h3 style={{ margin: "0 0 6px", fontWeight: 600, fontSize: 18, color: "#1a1a1a" }}>
                    Generating with AI
                </h3>
                <p style={{ color: "#888", fontSize: 13, margin: "0 0 20px" }}>
                    {GENERATING_MESSAGES[generatingStep]}
                </p>
                <div style={{
                    width: "100%", height: 6, borderRadius: 3,
                    background: "#f0f0f0", overflow: "hidden"
                }}>
                    <div style={{
                        width: `${progress}%`, height: "100%", borderRadius: 3,
                        background: "linear-gradient(90deg, #722ed1, #eb2f96)",
                        transition: "width 0.6s ease"
                    }} />
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: "#bbb" }}>
                    {Math.round(progress)}% · Please wait...
                </div>
            </Modal>

            <AIGeneratorConfig 
                visible={configVisible} 
                onClose={() => setConfigVisible(false)} 
                defaultPrompt={systemPromptDefault}
                contextInstructions={contextInstructions}
                category={category}
                excludeContextFields={excludeContextFields}
            />
        </>
    );
};

export default AIGenerateAction;
